/** @jsx jsx */
import {css, jsx} from '@emotion/react';
import React, { useRef, useState, createRef, SetStateAction} from 'react'; 

import { Button, Icon, Position, Toaster, Intent, ProgressBar, Overlay, Classes, Spinner} from '@blueprintjs/core'; 
import { uploadFile, isFileValid, convertToMB } from '../utils';
import classNames from "classnames";
import { ProgressBar as FormProgressBar} from './ProgressBar.js'; 

import { DefinitionProps, ColorProps, UploadConstraintProps } from "../interfaces"; 

// References: https://blog.logrocket.com/create-a-drag-and-drop-component-with-react-dropzone/ 
export const Upload: React.FC<UploadProps> = ({ 
        uploadConstraints,
        colors,
        setDil,
        setStep
    }) => {

    const [ fileUploaded, setFileUploaded ] = useState<File>(null); 
    const [ uploading, setUploading ] = useState(false) 
    const uploadButton = useRef<HTMLInputElement>(null); 

    const showToast = (errorMessage) => {
        AppToaster.show({
            icon: "warning-sign",
            message: errorMessage, 
            intent: Intent.DANGER
        });
    }

    const handleUpload = (userSelectedFile: File[]) => {
        const [_, errorMessage] = isFileValid(userSelectedFile[0], uploadConstraints);
        !errorMessage ? setFileUploaded(userSelectedFile[0]) : showToast(errorMessage);
    };

    const removeFile = () => {
        setFileUploaded(null);
    }

    const handleUploadButton = async () => {
        try {
            setUploading(true); 
            setDil((await uploadFile(fileUploaded) as DefinitionProps))
            setStep("View/Edit Schema")
            return
        } catch (err) {
            showToast('Server Error: ' + err.message); 
            setUploading(false); 
        }
    }

    return (
        <div>
            <FormProgressBar step={0} />
            <div css={uploadContainerCSS}>
                <div css={fileUploadContainerCSS} style={{backgroundColor: colors.background}}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()} 
                    onDragLeave={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        handleUpload(Array.from(e.dataTransfer.files)); 
                    }}
                >
                    <br />
                    <Icon icon="cloud-upload" iconSize={44} css={{color: colors.text}}/>
                    <h3 css={{color: colors.text}}>Drag and Drop Your File(s) Here</h3>
                    <p css={{color: colors.text}}>or</p>
                    <input type="file" id="uploadButton" hidden ref={uploadButton} onChange={(e) => {
                        e.preventDefault(); 
                        handleUpload(Array.from(e.target.files)); 
                    }}/>
                    <Button onClick={() => uploadButton.current?.click() } css={{width:132}}>Browse Files</Button>
                </div>
                <p css={constraintTextCSS} style={{color: colors.text}}>
                    Max File Size: {uploadConstraints.maxFileSize_mb }MB&nbsp;&nbsp;&nbsp;&nbsp;
                    Max File Amount: { uploadConstraints.maxNumberOfFiles }&nbsp;&nbsp;&nbsp;&nbsp;
                    File Type(s): { uploadConstraints.fileExtensionsAllowed.join(', ') }&nbsp;&nbsp;&nbsp;&nbsp;
                    Header Row Required: { uploadConstraints.headerRowRequired }
                </p>
                { fileUploaded && fileUploaded.name && fileUploaded.size &&
                    <div>
                        <table css={fileTableCSS}>
                            <tbody>
                                <tr css={fileTableRowCSS}>
                                        <td className="left">
                                            <Icon icon="document" iconSize={16} css={{color: colors.text}}/>&nbsp;&nbsp;
                                            {fileUploaded.name}
                                        </td>
                                        <td className="right">
                                            {convertToMB(fileUploaded, true)} MB
                                            <div css={{display: "inline", cursor: !uploading ? "pointer" : "not-allowed"}} onClick={(e) => {
                                                e.preventDefault(); 
                                                if (!uploading)
                                                    removeFile()
                                            }}>
                                                <Icon icon="cross" iconSize={16} css={{color: colors.text}}/>
                                            </div>
                                        </td>
                                    </tr>
                            </tbody>
                        </table> 
                        <br />
                        <Button css={css`float: right`} icon="upload" onClick={handleUploadButton} disabled={uploading} intent="primary" outlined={true}>Upload</Button>
                    </div>
                }
            </div>
            <div css={{width: '100%', margin: 'auto', alignItems: 'center', display: 'inline-block', justifyContent: 'center'}}>
                { uploading &&  <Spinner /> }
            </div>
        </div> 
    );
}

 
interface UploadProps {
    uploadConstraints: UploadConstraintProps,
    colors: ColorProps,
    setDil: React.Dispatch<SetStateAction<DefinitionProps>>,
    setStep: React.Dispatch<SetStateAction<string>>
} 

const classes = classNames(
    Classes.CARD,
    Classes.ELEVATION_4,
    "docs-overlay-example-transition",
    "docs-overlay-example-tall",
);

/** Singleton toaster instance. Create separate instances for different options. */
const AppToaster = Toaster.create({
    className: "recipe-toaster",
    position: Position.TOP
});


const progressBarCSS = css`
    width: 75%; 
    height: 127px; 
    padding-top: 40px;
    padding: 50px; 
    top: 200px;
    left: 100px; 
`;

const uploadContainerCSS = css`
    width: 80%;
    text-align: center;
    align-items: center;
    padding-top: 25px;
    margin: auto;
    width: 80%; 
`;

const fileUploadContainerCSS = css`
    display: inline-block;
    align-items: center;
    justify-content: center;
    margin: 0;
    width: 100%;
    height: 200px;
    border: 1px dashed grey;
    text-align: center; 
    border: 1px dashed #5C7080;
    border-radius: 4px;
`;

const constraintTextCSS = css`
    font-size: 14px;
    letter-spacing: 1px;
    text-align: center;
    line-height: 26px;
    maxWidth: 762px; 
`; 

const fileTableCSS = css`
    background: #F2F2F2;
    border-radius: 3px;
    width: 100%;
`;

const fileTableRowCSS = css`
    height: 36px;

    .left {
        float: left; 
        padding: 10px; 
    }

    .right {
        float: right; 
        text-align: right;
        display: inline;
        padding: 10px;
    }
`; 